import Avatar from '@/components/Avatar'
import IcoMoon from '@/components/IcoMoon'
import Layout, { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import Link from 'next/link'
import * as styles from './style'
import { getConfig } from '@/lib/config'
import { ItemType } from '@/shared/api-generated-types'
import { removeBookmark } from '@/lib/bookmark'
import { useBookmarkItems } from '@/context/bookmarkedItems'

export interface Props {
  durationSeconds?: number
  readTimeInSeconds?: number
  guideAvatarUrl?: string
  guideId?: string
  guideName?: string
  id?: string
  imageUrl?: string
  maxWidthImageUrl?: string
  slug?: string
  title?: string
  type: ItemType
  onRemove?: (id: string) => void
}

export const Divider = (): JSX.Element => <hr className={styles.divider} />

const BookmarkedItem = ({
  durationSeconds,
  readTimeInSeconds,
  guideAvatarUrl,
  guideName,
  id,
  imageUrl,
  slug,
  title,
  type,
  onRemove,
}: Props): JSX.Element => {
  const config = getConfig()
  const { dispatch } = useBookmarkItems()
  const classDetailsPageLink = `${config.publicRuntimeConfig.website.url}/classes/${slug}/${id}`
  const articleDetailsPageLink = `${config.publicRuntimeConfig.website.url}/articles/article/${id}`

  return (
    <>
      <Link href={type === ItemType.Class ? classDetailsPageLink : articleDetailsPageLink} className={styles.root}>
        <Layout spacing="sm">
          <Stack x justifyContent="center" alignItems="center">
            <Stack y className={styles.imageWrapper} alignSelf="flex-start">
              <div className={styles.image}>
                <img src={imageUrl ?? '/images/image-placeholder-large.png'} alt={title} loading="lazy" />
                <div className={styles.imageDescriptor}>
                  {type === 'CLASS' ? <IcoMoon icon="youtube" size={16} /> : <IcoMoon icon="book" size={16} />}
                </div>
              </div>
            </Stack>
            <Stack y className={styles.text}>
              {title ? (
                <Text
                  size="xxs"
                  responsiveSize={{ sm: 'xs', lg: 'md' }}
                  weight="lg"
                  tag="strong"
                  className={styles.truncate}
                >
                  {title}
                </Text>
              ) : null}
              <Text
                size="xxxs"
                responsiveSize={{ sm: 'xxxs', lg: 'xs' }}
                weight="sm"
                className={`${styles.truncate} ${styles.subtitle}`}
              >
                <Avatar
                  size="xs"
                  src={guideAvatarUrl ?? '/images/image-placeholder-large.png'}
                  className={styles.guidePhoto}
                />
                {guideName}
              </Text>
              <Stack x alignItems="center">
                <Stack x className={styles.detailsContainer} alignItems="center">
                  {durationSeconds && type === ItemType.Class ? (
                    <>
                      <IcoMoon className={`mr-xxs ${styles.calendarIcon}`} size={16} icon={'youtube'} />
                      <Text size="xxxs" responsiveSize={{ sm: 'xxxs', lg: 'xs' }} weight="lg">
                        {Math.ceil(durationSeconds / 60)} min
                      </Text>
                    </>
                  ) : null}
                  {readTimeInSeconds && type === ItemType.Article ? (
                    <>
                      <IcoMoon className={`mr-xxs ${styles.calendarIcon}`} size={16} icon={'book'} />
                      <Text size="xxxs" responsiveSize={{ sm: 'xxxs', lg: 'xs' }} weight="lg">
                        {Math.ceil(readTimeInSeconds / 60)} min
                      </Text>
                    </>
                  ) : null}
                </Stack>
              </Stack>
            </Stack>
            <Stack>
              <div className="relative group cursor-pointer">
                <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-black text-white text-xxs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity z-10 whitespace-nowrap pointer-events-none">
                  This will remove the bookmark
                </div>
                <IcoMoon
                  size={20}
                  icon="close"
                  className="text-gray-500"
                  onClick={async (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (id) {
                      await removeBookmark(id)
                      switch (type) {
                        case ItemType.Class:
                          dispatch({ type: 'REMOVE_CLASS', payload: { itemId: id } })
                          break
                        case ItemType.Article:
                          dispatch({ type: 'REMOVE_ARTICLE', payload: { itemId: id } })
                          break
                      }
                      if (onRemove) onRemove(id)
                    }
                  }}
                />
              </div>
            </Stack>
          </Stack>
        </Layout>
      </Link>
      <Divider />
    </>
  )
}

export default BookmarkedItem
